import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { Rule } from "@/types/role.types.ts";
import {
  ApiWorkflowBlockActionCandidateEmail,
  ApiWorkflowBlockActionCandidateStageChange,
  ApiWorkflowBlockActionGoogleWorkspace,
  ApiWorkflowBlockActionHTTPRequest,
  ApiWorkflowBlockActionInvitation,
  ApiWorkflowBlockActionNotification,
  ApiWorkflowBlockActionPerformanceReviewEnroll,
  ApiWorkflowBlockActionPerformanceReview,
  ApiWorkflowBlockActionRunWorkflow,
  ApiWorkflowBlockActionSurvey,
  ApiWorkflowBlockActionSurveyEnroll,
  ApiWorkflowBlockActionTask,
  ApiWorkflowBlockActionApproval,
  ApiWorkflowBlockActionEsign,
  ApiWorkflowBlockActionMeeting,
} from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowBlockDelayReference,
  ApiWorkflowBlockTypes,
  ApiWorkflowEventTypes,
  ApiWorkflowTriggerScheduleDayType,
  ApiWorkflowTriggerScheduleEntityField,
  ApiWorkflowTriggerScheduleEntityType,
  ApiWorkflowTriggerScheduleTimezoneSource,
  ApiWorkflowTriggerType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowCondition } from "@/types/workflows/workflow.condition.types.ts";
import { ApiWorkflowFolderShort } from "@/types/workflows/workflow-folder.types.ts";

export interface ApiWorkflowShort {
  id: number;
  title: string;
  emoji: string | null;
  description: string | null;
  public_title: string | null;
  public_description: string | null;
}

export interface ApiWorkflow {
  id: number;
  title: string;
  description: string | null;
  current_version_id: number;
  trigger_type: ApiWorkflowTriggerType;
  is_active: boolean;
  folder: ApiWorkflowFolderShort;
  emoji: string | null;
  public_title: string | null;
  public_description: string | null;
  is_shared: boolean;

  // trigger_conditions: ApiWorkflowCondition[];
  apply_to_rules: Rule[];
}

export interface ApiWorkflowBlockEdge {
  id: number;
  from_block_id: number;
  to_block_id: number;
  priority: number;

  conditions: ApiWorkflowCondition[];
}

export interface ApiWorkflowBlockMove {
  parent_block_id: number;
  position: number;
}

export interface ApiWorkflowBlock {
  id: number;
  action_type: ApiWorkflowBlockActionTypes;
  block_type: ApiWorkflowBlockTypes;
  title: string | null;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  delay_reference: ApiWorkflowBlockDelayReference;
  delay_day_type: ApiWorkflowTriggerScheduleDayType;

  action_notification: ApiWorkflowBlockActionNotification | null;
  action_http_request: ApiWorkflowBlockActionHTTPRequest | null;
  action_google_workspace: ApiWorkflowBlockActionGoogleWorkspace | null;
  action_invitation: ApiWorkflowBlockActionInvitation | null;
  action_survey: ApiWorkflowBlockActionSurvey | null;
  action_task: ApiWorkflowBlockActionTask | null;
  action_performance_review: ApiWorkflowBlockActionPerformanceReview | null;
  action_performance_review_enroll: ApiWorkflowBlockActionPerformanceReviewEnroll | null;
  action_survey_enroll: ApiWorkflowBlockActionSurveyEnroll | null;
  action_run_workflow: ApiWorkflowBlockActionRunWorkflow | null;
  action_survey_with_approval: ApiWorkflowBlockActionApproval | null;
  action_esign: ApiWorkflowBlockActionEsign | null;
  action_meeting: ApiWorkflowBlockActionMeeting | null;
  action_candidate_email: ApiWorkflowBlockActionCandidateEmail | null;
  action_candidate_stage_change: ApiWorkflowBlockActionCandidateStageChange | null;
}

export interface ApiWorkflowBlockShort {
  id: number;
  action_type: ApiWorkflowBlockActionTypes;
  block_type: ApiWorkflowBlockTypes;
  title: string | null;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  delay_reference: ApiWorkflowBlockDelayReference;
  delay_day_type: ApiWorkflowTriggerScheduleDayType;
}

export interface ApiWorkflowBlockEdgeShort {
  id: number;
  from_block_id: number;
  to_block_id: number;
  priority: number;
  has_conditions: boolean;
}

export interface ApiWorkflowTriggerAbstract {
  trigger_conditions: ApiWorkflowCondition[];
  trigger_schedule_rule: ApiWorkflowTriggerScheduleRule | null;
  trigger_event: ApiWorkflowTriggerEvent | null;
  trigger_recurring_rule: ApiWorkflowTriggerRecurringRule | null;
}

export interface ApiWorkflowVersion extends ApiWorkflowTriggerAbstract {
  id: number;
  workflow_id: number;
  blocks: ApiWorkflowBlock[];
  edges: ApiWorkflowBlockEdge[];
}

export interface ApiWorkflowTriggerScheduleRule {
  entity_type: ApiWorkflowTriggerScheduleEntityType;
  entity_field: ApiWorkflowTriggerScheduleEntityField;
  day_type: ApiWorkflowTriggerScheduleDayType;
  offset_days: number;
  offset_hours: number;
  trigger_hour_local: number;
  timezone_source: ApiWorkflowTriggerScheduleTimezoneSource;
  timezone_name: string | null;
  entity_field_id: number | null;
  is_annual: boolean;
}

export interface ApiWorkflowTriggerEvent {
  event_type: ApiWorkflowEventTypes;
}

export enum RecurringFrequency {
  daily = "DAILY",
  weekly = "WEEKLY",
  monthly = "MONTHLY",
  yearly = "YEARLY",
}

export enum WeekDay {
  mo = "MO",
  tu = "TU",
  we = "WE",
  th = "TH",
  fr = "FR",
  sa = "SA",
  su = "SU",
}

export enum CalendarRuleType {
  month_day = "month_day",
  ordinal_weekday = "ordinal_weekday",
}

export enum EndType {
  never = "never",
  until = "until",
  count = "count",
}

export const frequencies = Object.values(RecurringFrequency);
export const weekDays = Object.values(WeekDay);
export const calendarRuleTypes = Object.values(CalendarRuleType);
export const endTypes = Object.values(EndType);
export const ordinals = [1, 2, 3, 4, -1] as const;

export type Ordinal = (typeof ordinals)[number];

export interface WorkflowTriggerRecurringRuleForm {
  frequency: RecurringFrequency;
  interval: number;
  weeklyDays: WeekDay[];
  calendarRuleType: CalendarRuleType;
  monthDay: number;
  ordinal: Ordinal;
  ordinalWeekday: WeekDay;
  yearMonth: number;
  endType: EndType;
  until: Date | null;
  count: number | null;
  dateStart: Date;
  hour: number;
}

export interface WorkflowTriggerRecurringRuleRef {
  saveData: () => Promise<boolean>;
}

export interface WorkflowTriggerRecurringRuleProps {
  workflow: ApiWorkflow;
  rule: ApiWorkflowTriggerRecurringRule | null;
}

export interface ApiWorkflowTriggerRecurringRule {
  rrule: string;
  date_start: Date;
  hour: number;
}

export interface ApiWorkflowListItem extends ApiWorkflowTriggerAbstract {
  id: number;
  title: string;
  description: string | null;
  current_version_id: number;
  trigger_type: ApiWorkflowTriggerType;
  is_active: boolean;
  runs_count: number;
  datetime_last_run_at: Date | null;
  folder: ApiWorkflowFolderShort;
  action_count: number;
  actions: ApiWorkflowBlockShort[];
  edges: ApiWorkflowBlockEdgeShort[];
  start_block_id: number | null;
  emoji: string | null;
  is_shared: boolean;
}

export interface ApiWorkflowFilter extends ApiFilterBase {
  page_size?: number;
  text?: string;
  folder_id?: number | null;
}

export interface ApiWorkflowCreate {
  title: string;
  description: string | null;
  folder_id: number;
}

export interface ApiWorkflowUpdateDetails {
  title: string;
  description: string | null;
  folder_id: number;
  emoji: string | null;
  public_title: string | null;
  public_description: string | null;
  is_shared: boolean;
}

export interface ApiWorkflowUpdateTrigger {
  trigger_type: ApiWorkflowTriggerType;
  event_type: ApiWorkflowEventTypes | null;
  schedule_entity_type: ApiWorkflowTriggerScheduleEntityType | null;
  schedule_entity_field: ApiWorkflowTriggerScheduleEntityField | null;
}

export interface ApiWorkflowTriggerScheduleRuleSave {
  entity_type: ApiWorkflowTriggerScheduleEntityType;
  entity_field: ApiWorkflowTriggerScheduleEntityField;
  day_type: ApiWorkflowTriggerScheduleDayType;
  offset_days: number;
  offset_hours: number;
  trigger_hour_local: number;
  timezone_source: ApiWorkflowTriggerScheduleTimezoneSource;
  timezone_name: string | null;
  entity_field_id: number | null;
  is_annual: boolean;
}

export interface ApiWorkflowBlockSave {
  action_type: ApiWorkflowBlockActionTypes;
  block_type: ApiWorkflowBlockTypes;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  delay_reference: ApiWorkflowBlockDelayReference;
  delay_day_type: ApiWorkflowTriggerScheduleDayType;
  title: string | null;

  from_block_id: number;
}

export interface ApiWorkflowExecutionCounts {
  total: number;
  pending: number;
  running: number;
  completed: number;
  failed: number;
}

export interface ApiWorkflowSummary {
  id: number;
  title: string;
  description: string | null;
  current_version_id: number;
  trigger_type: ApiWorkflowTriggerType;
  is_active: boolean;
  runs_count: number;
  datetime_last_run_at: Date | null;
  folder: ApiWorkflowFolderShort;
  owner: ApiEmployee;
  trigger_event: ApiWorkflowTriggerEvent | null;
  trigger_schedule_rule: ApiWorkflowTriggerScheduleRule | null;
  trigger_recurring_rule: ApiWorkflowTriggerRecurringRule | null;
  trigger_conditions: ApiWorkflowCondition[];
  apply_to_rules: Rule[];
  execution_counts: ApiWorkflowExecutionCounts;
  action_count: number;
  datetime_created: Date | null;
  emoji: string | null;
  public_title: string | null;
  public_description: string | null;
  is_shared: boolean;
}

export interface ApiEmployeeWorkflowRunPayload {
  block_portal_access?: boolean;
  replace_existing_blockers?: boolean;
}

export type WorkflowRunStatus = "success" | "error";

export interface ApiEmployeeWorkflowRunResult {
  success?: boolean;
  execution_id?: number | null;
  blocking_execution_attached?: boolean;
  survey: {
    survey_id: number;
    employee_id: number;
  } | null;
}
